import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { NotificationPreferences } from '@/components/admin/NotificationPreferences';
import { PlanManagement } from '@/components/admin/PlanManagement';
import { PlanViewer } from '@/components/admin/PlanViewer';
import { PlanUpgradeRequests } from '@/components/admin/PlanUpgradeRequests';
import { DefaultBillingRatesSettings } from '@/components/admin/DefaultBillingRatesSettings';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Settings, Crown, Shield, Eye, ArrowUp, Trash2, RefreshCw, DollarSign } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const AdminSettingsPage = () => {
  const { user } = useAdminAuth();
  const [searchParams] = useSearchParams();
  
  // Check for impersonation mode - super admin features should be hidden when impersonating
  const isImpersonating = searchParams.get('impersonate') === 'agency';
  const isSuperAdmin = user?.role === 'super_admin' && !isImpersonating;
  const isAgencyAdmin = user?.role === 'agency_admin' || isImpersonating;
  
  // Get initial tab from URL query parameter, default to 'notifications'
  const tabFromUrl = searchParams.get('tab');
  const defaultTab = (() => {
    if (isSuperAdmin && (tabFromUrl === 'plans' || tabFromUrl === 'upgrade-requests' || tabFromUrl === 'billing-defaults')) {
      return tabFromUrl;
    }
    if (isAgencyAdmin && tabFromUrl === 'view-plans') {
      return 'view-plans';
    }
    return 'notifications';
  })();

  return (
    <>
      <Helmet>
        <title>Settings | CheckFirst Admin</title>
        <meta name="description" content="Manage your admin settings and notification preferences" />
      </Helmet>
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Settings className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Settings</h1>
                <p className="text-muted-foreground">Manage your preferences</p>
              </div>
            </div>
            {isSuperAdmin && (
              <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 gap-1">
                <Shield className="h-3 w-3" />
                Super Admin
              </Badge>
            )}
          </div>

          <Tabs defaultValue={defaultTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
              {isAgencyAdmin && (
                <TabsTrigger value="view-plans" className="gap-1">
                  <Eye className="h-3 w-3" />
                  Plans
                </TabsTrigger>
              )}
              {isSuperAdmin && (
                <>
                  <TabsTrigger value="billing-defaults" className="gap-1">
                    <DollarSign className="h-3 w-3" />
                    Billing Defaults
                  </TabsTrigger>
                  <TabsTrigger value="upgrade-requests" className="gap-1">
                    <ArrowUp className="h-3 w-3" />
                    Upgrade Requests
                  </TabsTrigger>
                  <TabsTrigger value="plans" className="gap-1">
                    <Crown className="h-3 w-3" />
                    Manage Plans
                  </TabsTrigger>
                </>
              )}
              <TabsTrigger value="debug" className="gap-1">
                <RefreshCw className="h-3 w-3" />
                Debug
              </TabsTrigger>
            </TabsList>

            <TabsContent value="notifications" className="space-y-6">
              <div className="max-w-2xl">
                <NotificationPreferences />
              </div>
            </TabsContent>

            {isAgencyAdmin && (
              <TabsContent value="view-plans" className="space-y-6">
                <PlanViewer />
              </TabsContent>
            )}

            {isSuperAdmin && (
              <>
                <TabsContent value="billing-defaults" className="space-y-6">
                  <Card className="border-purple-200 bg-purple-50/30 dark:border-purple-900 dark:bg-purple-950/20">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="h-4 w-4 text-purple-600" />
                        Platform-Wide Billing Defaults
                      </CardTitle>
                      <CardDescription>
                        Configure default billing rates used across all agencies when agency-specific rates are not set.
                      </CardDescription>
                    </CardHeader>
                  </Card>
                  <DefaultBillingRatesSettings />
                </TabsContent>

                <TabsContent value="upgrade-requests" className="space-y-6">
                  <PlanUpgradeRequests />
                </TabsContent>

                <TabsContent value="plans" className="space-y-6">
                  <Card className="border-purple-200 bg-purple-50/30 dark:border-purple-900 dark:bg-purple-950/20">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="h-4 w-4 text-purple-600" />
                        Super Admin Access
                      </CardTitle>
                      <CardDescription>
                        You have unrestricted access to manage all subscription plans. Changes here affect all agencies globally.
                      </CardDescription>
                    </CardHeader>
                  </Card>
                  <PlanManagement />
                </TabsContent>
              </>
            )}

            <TabsContent value="debug" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 text-primary" />
                    Cache & Storage
                  </CardTitle>
                  <CardDescription>
                    Clear cached data to resolve display issues or force a fresh session
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      variant="outline" 
                      className="gap-2"
                      onClick={() => {
                        localStorage.clear();
                        toast.success('Local storage cleared');
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      Clear Local Storage
                    </Button>
                    <Button 
                      variant="outline" 
                      className="gap-2"
                      onClick={() => {
                        sessionStorage.clear();
                        toast.success('Session storage cleared');
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      Clear Session Storage
                    </Button>
                    <Button 
                      variant="destructive" 
                      className="gap-2"
                      onClick={() => {
                        localStorage.clear();
                        sessionStorage.clear();
                        toast.success('All cache cleared - reloading...');
                        setTimeout(() => window.location.reload(), 500);
                      }}
                    >
                      <RefreshCw className="h-4 w-4" />
                      Clear All & Reload
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Note: Clearing all cache will sign you out and reload the page.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminSettingsPage;
